/**
 * TTN editor (2026-06-03).
 * Простий — поля перевізника, авто, водія, посилання на Реалізацію.
 * Без проводок.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tnn, type TnnCreate } from '@/api/tnn';
import { goodsSales } from '@/api/goodsSales';
import { orgs, contractors as contractorsApi, contractorNested } from '@/api/catalogs';
import { refs } from '@/api/refs';
import SearchSelect from '@/components/SearchSelect';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import { useConfirm } from '@/components/ConfirmProvider';
import { pickDefaultOrg } from '@/store/defaults';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';

function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}


interface FormState extends Omit<TnnCreate, 'document_date'> {
  document_date: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  goods_sale_id: '',
  shipping_address: '',
  shipping_address_2: '',
  destination_address: '',
  carrier_name: '',
  carrier_edrpou: '',
  vehicle_plate: '',
  vehicle_model: '',
  driver_full_name: '',
  driver_license_number: '',
  vehicle_storage_address: '',
  cargo_description: '',
  total_places: null,
  total_weight_kg: null,
  comment: '',
};


export default function TnnEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  // 2026-07-27 (рішення власника): вантажоодержувач + «заповнити з
  // реалізації» (попап проведених реалізацій контрагента) + попапи адрес.
  const [consigneeId, setConsigneeId] = useState('');
  const [salePickerOpen, setSalePickerOpen] = useState(false);
  const [shipPickerOpen, setShipPickerOpen] = useState(false);
  const [destPickerOpen, setDestPickerOpen] = useState(false);
  const [ship2PickerOpen, setShip2PickerOpen] = useState(false);
  // 0387: друге місце завантаження (догруз) — показ поля.
  const [showShip2, setShowShip2] = useState(false);
  // 2026-07-27 (рішення власника): створення складу/адреси ПРЯМО з попапів
  // ТТН (без переходу у довідники/картку контрагента).
  const [newWhOpen, setNewWhOpen] = useState(false);
  const [newWhName, setNewWhName] = useState('');
  const [newWhAddress, setNewWhAddress] = useState('');
  const [newAddrOpen, setNewAddrOpen] = useState(false);
  const [newAddrLabel, setNewAddrLabel] = useState('');
  const [newAddrText, setNewAddrText] = useState('');
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const salesQ = useQuery({
    queryKey: ['goods-sales-for-tnn', consigneeId],
    queryFn: () => goodsSales.list({
      posted: true, limit: 200,
      contractor_id: consigneeId || undefined,
    }),
  });
  const warehousesQ = useQuery({
    queryKey: ['warehouses', 'tnn'],
    queryFn: () => refs.warehouses(undefined),
  });
  const destAddrQ = useQuery({
    queryKey: ['contractor-delivery-addresses', consigneeId],
    queryFn: () => contractorNested.listDeliveryAddresses(consigneeId),
    enabled: !!consigneeId,
  });

  const docQuery = useQuery({
    queryKey: ['tnn', id],
    queryFn: () => tnn.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'tnn', (cid) => tnn.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = pickDefaultOrg(orgList.data?.items)?.id ?? '';
      const urlSaleId = searchParams.get('sale_id') ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        goods_sale_id: urlSaleId || f.goods_sale_id,
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        goods_sale_id: d.goods_sale_id,
        shipping_address: d.shipping_address ?? '',
        shipping_address_2: d.shipping_address_2 ?? '',
        destination_address: d.destination_address ?? '',
        carrier_name: d.carrier_name,
        carrier_edrpou: d.carrier_edrpou ?? '',
        vehicle_plate: d.vehicle_plate,
        vehicle_model: d.vehicle_model ?? '',
        driver_full_name: d.driver_full_name,
        driver_license_number: d.driver_license_number ?? '',
        vehicle_storage_address: d.vehicle_storage_address ?? '',
        cargo_description: d.cargo_description ?? '',
        total_places: d.total_places,
        total_weight_kg: d.total_weight_kg,
        comment: d.comment ?? '',
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, orgList.data, searchParams]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  // Автозаповнення з реалізації-підстави: організація, адреса завантаження
  // (адреса складу реалізації), адреса призначення (основна адреса
  // постачання контрагента), вантажоодержувач.
  async function applyFromSale(saleId: string) {
    try {
      const sale = await goodsSales.get(saleId);
      setConsigneeId(sale.contractor_id);
      const whs = await refs.warehouses(sale.organization_id);
      const wh = whs.find((w) => w.id === sale.warehouse_id);
      const addrs = await contractorNested
        .listDeliveryAddresses(sale.contractor_id).catch(() => []);
      const dest = addrs.find((a) => a.is_main)?.address
        ?? addrs[0]?.address
        ?? ((sale as { delivery_address?: string | null }).delivery_address ?? '');
      setForm((f) => ({
        ...f,
        organization_id: sale.organization_id,
        goods_sale_id: saleId,
        shipping_address: wh?.address || f.shipping_address,
        destination_address: dest || f.destination_address,
      }));
      setSalePickerOpen(false);
    } catch (e) {
      setError(extractError(e));
    }
  }

  // ?sale_id= з кнопки «Створити ТТН» реалізації — авто-застосування.
  const saleApplied = useRef(false);
  useEffect(() => {
    const urlSaleId = searchParams.get('sale_id');
    if (!isNew || !urlSaleId || saleApplied.current) return;
    saleApplied.current = true;
    void applyFromSale(urlSaleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew]);

  useEffect(() => {
    if (docQuery.data?.shipping_address_2) setShowShip2(true);
  }, [docQuery.data]);

  // Існуюча ТТН: вантажоодержувач з реалізації-підстави.
  useEffect(() => {
    if (isNew || !docQuery.data?.goods_sale_id || consigneeId) return;
    void goodsSales.get(docQuery.data.goods_sale_id)
      .then((sale) => setConsigneeId(sale.contractor_id))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, docQuery.data]);

  const create = useMutation({
    mutationFn: () => tnn.create({
      ...form,
      document_number: form.document_number || null,
      document_date: new Date(form.document_date).toISOString(),
      total_weight_kg: form.total_weight_kg ? String(form.total_weight_kg) : null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['tnn-list'] });
      navigate(`/tnn/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => tnn.update(id!, {
      ...form,
      document_date: new Date(form.document_date).toISOString(),
      total_weight_kg: form.total_weight_kg ? String(form.total_weight_kg) : null,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tnn', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: create → post одним кліком.
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await tnn.create({
        ...form,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        total_weight_kg: form.total_weight_kg ? String(form.total_weight_kg) : null,
      });
      try {
        await tnn.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/tnn/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['tnn-list'] });
      navigate(`/tnn/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: update → post одним кліком.
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await tnn.update(id!, {
        ...form,
        document_date: new Date(form.document_date).toISOString(),
        total_weight_kg: form.total_weight_kg ? String(form.total_weight_kg) : null,
      });
      await tnn.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tnn', id] });
      qc.invalidateQueries({ queryKey: ['tnn-list'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => tnn.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tnn', id] });
      qc.invalidateQueries({ queryKey: ['tnn-list'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => tnn.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tnn-list'] });
      navigate('/tnn');
    },
  });

  // ч.865 (новий дизайн, Закон #10): у ТТН «ще дій» не було — права колонка
  // без «Повʼязаних дій» (лише прогрес / звʼязані / історія).
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  // Прогрес — ЛИШЕ обовʼязкові поля: підстава-реалізація (без неї не
  // зберігається) + перевізник / номер авто / ПІБ водія (без них не проводиться).
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'party', title: 'Вантажоодержувач і підстава', required: 1,
      filled: form.goods_sale_id ? 1 : 0 },
    { id: 'addr', title: 'Адреси', required: 0, filled: 0 },
    { id: 'carrier', title: 'Перевізник, авто і водій', required: 3,
      filled: (form.carrier_name ? 1 : 0) + (form.vehicle_plate ? 1 : 0) + (form.driver_full_name ? 1 : 0) },
    { id: 'cargo', title: 'Вантаж', required: 0, filled: 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова ТТН' : `ТТН: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
          {/* 2026-07-28 (Закони дизайну #6/#7): DocumentToolbar замість
              кастомних кнопок з емодзі; друк — для БУДЬ-ЯКОГО збереженого
              документа (не лише проведеного) через SPA-обгортку з токеном. */}
          <DocumentToolbar
            isNew={isNew}
            isPosted={isPosted}
            onBackToList={() => navigate('/tnn')}
            onSave={() => isNew ? create.mutate() : update.mutate()}
            // ТТН без реалізації-підстави не існує (goods_sale_id обовʼязковий).
            saveDisabled={!form.goods_sale_id}
            savePending={create.isPending || update.isPending}
            onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
            postPending={createAndPost.isPending || updateAndPost.isPending}
            postDisabled={!form.goods_sale_id || !form.carrier_name
              || !form.vehicle_plate || !form.driver_full_name}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm('Видалити ТТН?').then((ok) => { if (ok) remove.mutate(); }); }}
            deletePending={remove.isPending}
            onPrint={() => navigate(`/tnn/${id}/print`)}
            entriesDoc={{ type: 'doc.tnn', id: isNew ? '' : id! }}
            railMode
            extraActions={railActions}
          />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/tnn/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.tnn', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id ?? ''}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew || isPosted}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ ТТН{' '}
            {isNew && <small style={{ color: 'var(--muted)' }}>(auto)</small>}
          </label>
          <input value={form.document_number ?? ''}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 disabled={isPosted}
                 placeholder={isNew ? 'ТТН-0001-2026 (auto)' : undefined} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Вантажоодержувач і підстава">
      {/* 2026-07-27: рядок 2 як у реалізації — вантажоодержувач + попап підстав. */}
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 10' }}>
          <label>Вантажоодержувач (покупець)</label>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={consigneeId}
            onChange={(cid) => setConsigneeId(cid)}
            disabled={isPosted || !!form.goods_sale_id}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          {/* 2026-07-31 (рішення власника): звʼязок-підстава блокує
              вантажоодержувача; ч.865: «Підстава» — назва поля, кнопка — лише
              поки звʼязку немає, × знімає звʼязок (як у поверненнях, ч.861). */}
          <label>{form.goods_sale_id ? 'Підстава *' : '\u00A0'}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {!form.goods_sale_id && (
            <button type="button" className="btn-doc"
              onClick={() => setSalePickerOpen(true)}
              disabled={isPosted}
              title="Обрати проведену реалізацію: заповнить організацію та адреси">
              <DocIcon />заповнити з реалізації
            </button>
            )}
            {form.goods_sale_id && (() => {
              const basisSale = salesQ.data?.items.find((x) => x.id === form.goods_sale_id);
              return (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                    onClick={() => navigate(`/goods-sales/${form.goods_sale_id}`)}>
                    реалізація{basisSale
                      ? ` №${basisSale.document_number} від ${new Date(basisSale.document_date).toLocaleDateString('uk-UA')}`
                      : ''}
                  </a>
                  {!isPosted && (
                    <button type="button" className="btn-x"
                      onClick={() => setForm({ ...form, goods_sale_id: '' })}
                      title="Зняти звʼязок з реалізацією">×</button>
                  )}
                </span>
              );
            })()}
          </div>
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Адреси">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Адреса завантаження</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <input value={form.shipping_address ?? ''}
                   onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                   disabled={isPosted}
                   placeholder="Звідки везуть товар" style={{ flex: 1 }} />
            <button type="button" onClick={() => setShipPickerOpen(true)} disabled={isPosted}
                    title="Обрати адресу нашого складу">обрати</button>
          </div>
          {!showShip2 && !isPosted && (
            <button type="button" style={{ marginTop: 4, fontSize: '0.82rem' }}
              onClick={() => setShowShip2(true)}
              title="Догруз з другого складу — друге місце завантаження у ТТН">
              + додати місце догрузу
            </button>
          )}
          {showShip2 && (
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              <input value={form.shipping_address_2 ?? ''}
                     onChange={(e) => setForm({ ...form, shipping_address_2: e.target.value })}
                     disabled={isPosted}
                     placeholder="Додаткове місце завантаження (догруз)" style={{ flex: 1 }} />
              <button type="button" onClick={() => setShip2PickerOpen(true)} disabled={isPosted}
                      title="Обрати адресу нашого складу">обрати</button>
              {!isPosted && (
                <button type="button"
                        onClick={() => { setShowShip2(false); setForm({ ...form, shipping_address_2: '' }); }}
                        title="Прибрати місце догрузу">×</button>
              )}
            </div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Адреса призначення</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <input value={form.destination_address ?? ''}
                   onChange={(e) => setForm({ ...form, destination_address: e.target.value })}
                   disabled={isPosted}
                   placeholder="Куди доставити" style={{ flex: 1 }} />
            <button type="button" onClick={() => setDestPickerOpen(true)}
                    disabled={isPosted || !consigneeId}
                    title={consigneeId
                      ? 'Обрати адресу постачання вантажоодержувача'
                      : 'Спершу оберіть вантажоодержувача'}>обрати</button>
          </div>
        </div>
      </div>

      </DocBlock>

      <DocBlock index={4} title="Перевізник, авто і водій">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 8' }}>
          <label>Найменування перевізника *</label>
          <input value={form.carrier_name}
                 onChange={(e) => setForm({ ...form, carrier_name: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>ЄДРПОУ перевізника</label>
          <input value={form.carrier_edrpou ?? ''}
                 onChange={(e) => setForm({ ...form, carrier_edrpou: e.target.value })}
                 disabled={isPosted}
                 maxLength={10} />
        </div>
      </div>

      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Реєстр. номер авто *</label>
          <input value={form.vehicle_plate}
                 onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value })}
                 disabled={isPosted}
                 placeholder="АА1234ВВ" />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Марка / модель авто</label>
          <input value={form.vehicle_model ?? ''}
                 onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 8' }}>
          <label>ПIБ водія *</label>
          <input value={form.driver_full_name}
                 onChange={(e) => setForm({ ...form, driver_full_name: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ посвідчення</label>
          <input value={form.driver_license_number ?? ''}
                 onChange={(e) => setForm({ ...form, driver_license_number: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>

      {/* Наказ №1727 (з 26.07.2026): обов'язковий реквізит форми ТТН. */}
      <div className="field">
        <label>Місце зберігання транспортного засобу{' '}
          <small style={{ color: 'var(--muted)' }}>
            (адреса перевізника, де фактично зберігається авто — форма ТТН з 26.07.2026)
          </small>
        </label>
        <input value={form.vehicle_storage_address ?? ''}
               onChange={(e) => setForm({ ...form, vehicle_storage_address: e.target.value })}
               disabled={isPosted} maxLength={500} />
      </div>

      </DocBlock>

      <DocBlock index={5} title="Вантаж">
      <div className="field">
        <label>Опис вантажу</label>
        <input value={form.cargo_description ?? ''}
               onChange={(e) => setForm({ ...form, cargo_description: e.target.value })}
               disabled={isPosted}
               placeholder="Узагальнений опис (необов'язково)" />
      </div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Кількість місць</label>
          <input type="number" min="0" value={form.total_places ?? ''}
                 onChange={(e) => setForm({ ...form, total_places: e.target.value ? Number(e.target.value) : null })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Вага брутто, кг</label>
          <input type="number" step="0.001" min="0" value={form.total_weight_kg ?? ''}
                 onChange={(e) => setForm({ ...form, total_weight_kg: e.target.value || null })}
                 disabled={isPosted} />
        </div>
      </div>

      </DocBlock>

      <DocBlock index={6} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment ?? ''}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={'doc.tnn'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>

      {salePickerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setSalePickerOpen(false)}>
          <div className="card" style={{ width: 640, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Проведені реалізації{consigneeId ? ' вантажоодержувача' : ''}</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setSalePickerOpen(false)}>закрити</button>
            </div>
            {(salesQ.data?.items.length ?? 0) === 0 && (
              <p style={{ color: 'var(--muted)' }}>Немає проведених реалізацій.</p>
            )}
            <table className="tch">
              <tbody>
                {salesQ.data?.items.map((sl) => (
                  <tr key={sl.id} onClick={() => void applyFromSale(sl.id)}
                      style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-soft)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                    <td>{sl.document_number}</td>
                    <td>
                      {new Date(sl.document_date).toLocaleDateString('uk-UA')}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {Number(sl.total_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {(shipPickerOpen || ship2PickerOpen) && (() => {
        const isSecond = ship2PickerOpen;
        const closeBoth = () => { setShipPickerOpen(false); setShip2PickerOpen(false); };
        const withAddress = (warehousesQ.data ?? [])
          .filter((w) => !form.organization_id || w.organization_id === form.organization_id)
          .filter((w) => (w.address ?? '').trim().length > 0);
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}
               onClick={closeBoth}>
            <div className="card" style={{ width: 560, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
                 onClick={(e) => e.stopPropagation()}>
              <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>Адреси наших складів{isSecond ? ' (догруз)' : ''}</h3>
                <button style={{ marginLeft: 'auto' }} onClick={closeBoth}>закрити</button>
              </div>
              {withAddress.length === 0 && (
                <p style={{ color: 'var(--muted)' }}>
                  Немає складів із заповненою адресою.
                </p>
              )}
              {withAddress.map((w) => (
                <div key={w.id}
                     onClick={() => {
                       setForm(isSecond
                         ? { ...form, shipping_address_2: w.address ?? '' }
                         : { ...form, shipping_address: w.address ?? '' });
                       closeBoth();
                     }}
                     style={{ padding: '0.4rem 0.5rem', cursor: 'pointer',
                              borderBottom: '1px solid var(--border-soft)' }}
                     onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                     onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                  <strong>{w.name}</strong>
                  <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{w.address}</div>
                </div>
              ))}
              {!newWhOpen ? (
                <button type="button" style={{ marginTop: '0.6rem' }}
                  onClick={() => setNewWhOpen(true)}
                  disabled={!form.organization_id}
                  title={form.organization_id
                    ? 'Створити склад з адресою, не виходячи з ТТН'
                    : 'Спершу оберіть організацію'}>
                  + додати склад
                </button>
              ) : (
                <fieldset style={{ marginTop: '0.6rem', padding: '0.6rem', border: '1px solid var(--border)' }}>
                  <legend style={{ fontSize: '0.85rem' }}>Новий склад</legend>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.5rem' }}>
                    <input value={newWhName} onChange={(e) => setNewWhName(e.target.value)}
                      placeholder="Назва складу *" />
                    <input value={newWhAddress} onChange={(e) => setNewWhAddress(e.target.value)}
                      placeholder="Адреса *" maxLength={500} />
                  </div>
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                    <button type="button" className="primary"
                      disabled={!newWhName.trim() || newWhAddress.trim().length < 3}
                      onClick={() => {
                        void refs.createWarehouse({
                          organization_id: form.organization_id,
                          code: null, name: newWhName.trim(),
                          address: newWhAddress.trim(),
                        }).then((w) => {
                          setForm((f) => isSecond
                            ? { ...f, shipping_address_2: w.address ?? '' }
                            : { ...f, shipping_address: w.address ?? '' });
                          setNewWhOpen(false); setNewWhName(''); setNewWhAddress('');
                          void warehousesQ.refetch();
                          closeBoth();
                        }).catch((e) => setError(extractError(e)));
                      }}>
                      створити і підставити
                    </button>
                    <button type="button" onClick={() => setNewWhOpen(false)}>скасувати</button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        );
      })()}
      {destPickerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setDestPickerOpen(false)}>
          <div className="card" style={{ width: 560, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Адреси постачання вантажоодержувача</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setDestPickerOpen(false)}>закрити</button>
            </div>
            {(destAddrQ.data?.length ?? 0) === 0 && (
              <p style={{ color: 'var(--muted)' }}>
                У картці контрагента немає адрес постачання
                (вкладка «Адреси постачання»).
              </p>
            )}
            {destAddrQ.data?.map((a) => (
              <div key={a.id}
                   onClick={() => { setForm({ ...form, destination_address: a.address }); setDestPickerOpen(false); }}
                   style={{ padding: '0.4rem 0.5rem', cursor: 'pointer',
                            borderBottom: '1px solid var(--border-soft)' }}
                   onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                   onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                <strong>{a.name ?? 'Адреса'}{a.is_main ? ' (осн.)' : ''}</strong>
                <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{a.address}</div>
              </div>
            ))}
            {!newAddrOpen ? (
              <button type="button" style={{ marginTop: '0.6rem' }}
                onClick={() => setNewAddrOpen(true)}
                title="Створити адресу постачання контрагента, не виходячи з ТТН">
                + додати адресу
              </button>
            ) : (
              <fieldset style={{ marginTop: '0.6rem', padding: '0.6rem', border: '1px solid var(--border)' }}>
                <legend style={{ fontSize: '0.85rem' }}>Нова адреса постачання</legend>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.5rem' }}>
                  <input value={newAddrLabel} onChange={(e) => setNewAddrLabel(e.target.value)}
                    placeholder="Назва (напр. Склад Київ)" />
                  <input value={newAddrText} onChange={(e) => setNewAddrText(e.target.value)}
                    placeholder="Адреса *" maxLength={500} />
                </div>
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                  <button type="button" className="primary"
                    disabled={newAddrText.trim().length < 3}
                    onClick={() => {
                      void contractorNested.createDeliveryAddress({
                        contractor_id: consigneeId,
                        name: newAddrLabel.trim() || null,
                        address: newAddrText.trim(),
                        is_main: (destAddrQ.data?.length ?? 0) === 0,
                      }).then((a) => {
                        setForm((f) => ({ ...f, destination_address: a.address }));
                        setNewAddrOpen(false); setNewAddrLabel(''); setNewAddrText('');
                        void destAddrQ.refetch();
                        setDestPickerOpen(false);
                      }).catch((e) => setError(extractError(e)));
                    }}>
                    створити і підставити
                  </button>
                  <button type="button" onClick={() => setNewAddrOpen(false)}>скасувати</button>
                </div>
              </fieldset>
            )}
          </div>
        </div>
      )}

      {error && <div className="error">{error}</div>}
    </div>
  );
}


function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }> } } }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1]) : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}
